<template>
  <v-navigation-drawer
    :value="value"
    @input="$emit('input', $event)"
    fixed
    temporary
    right
    :stateless="keepOpen"
    :width="$vuetify.breakpoint.xsOnly ? '100%' : 460"
  >
    <v-toolbar color="purple" dark dense flat>
      <v-icon left>mdi-rocket-launch</v-icon>
      <v-toolbar-title class="subtitle-1">{{ $t('swot.initiatives') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
    </v-toolbar>

    <div v-if="factor" class="pa-3">
      <!-- Identificação do fator -->
      <div class="d-flex align-center mb-1">
        <v-chip x-small dark :color="typeColor" class="mr-2">{{ typeLabel }}</v-chip>
        <span class="font-weight-bold">{{ factor.code || factor.id }}</span>
      </div>
      <div class="body-2 mb-3">{{ factor.description }}</div>
      <v-divider class="mb-3"></v-divider>

      <v-alert v-if="error" type="error" text dense>
        {{ $t('swot.loadInitiativesError') }}
        <v-btn text x-small color="error" @click="$emit('retry')">{{ $t('swot.retry') }}</v-btn>
      </v-alert>
      <v-skeleton-loader v-else-if="loading" type="card-heading, list-item-two-line@2"></v-skeleton-loader>
      <div v-else-if="initiatives.length === 0" class="text-center grey--text py-6">
        {{ $t('swot.noInitiatives') }}
      </div>

      <template v-else>
        <div class="caption grey--text mb-2">{{ $tc('swot.linkedInitiatives', initiatives.length, { n: initiatives.length }) }}</div>
        <v-card v-for="item in initiatives" :key="item.id" outlined class="mb-2">
          <v-card-text class="pa-3">
            <div class="d-flex align-start">
              <div class="body-2 font-weight-medium mr-2" style="flex: 1 1 auto; min-width: 0;">{{ item.goal }}</div>
              <v-chip x-small label dark :color="statusColor(item.status)">{{ item.status }}</v-chip>
            </div>

            <div class="caption mt-1">
              <span class="grey--text">{{ $t('swot.objective') }}:</span>
              <v-progress-circular v-if="objectivesLoading" indeterminate size="10" width="1" class="ml-1"></v-progress-circular>
              <span v-else>{{ objectiveLabel(item.objectiveId) }}</span>
            </div>

            <div class="caption">
              <span class="grey--text">{{ $t('swot.method') }}:</span> {{ item.method || '—' }}
              <template v-if="item.method === 'Quantitativo'">
                · <span class="grey--text">{{ $t('swot.target') }}:</span> {{ item.targetValue != null ? item.targetValue : '—' }}
                · <span class="grey--text">{{ $t('swot.result') }}:</span> {{ item.resultValue != null ? item.resultValue : '—' }}
              </template>
            </div>

            <!-- Percentual gravado pelo formulário da iniciativa (mesma regra de cálculo) -->
            <div class="d-flex align-center mt-2">
              <v-progress-linear :value="item.completionPercentage || 0" height="8" color="success" rounded></v-progress-linear>
              <span class="caption ml-2">{{ item.completionPercentage || 0 }}%</span>
            </div>

            <div class="d-flex align-center mt-1">
              <span class="caption grey--text">{{ $tc('swot.actionPlans', (item.plans || []).length, { n: (item.plans || []).length }) }}</span>
              <v-spacer></v-spacer>
              <v-btn text x-small color="purple" @click="$emit('edit', item)">
                <v-icon x-small left>mdi-pencil</v-icon>{{ $t('swot.openInitiative') }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </template>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { initiativeStatusColor } from '@/helper/initiativeStatus';

// Tipo do fator (factor.type no backend) -> chave de tradução e cor do quadrante
const FACTOR_TYPES = {
  strengths: { key: 'swot.strength', color: 'green darken-2' },
  weaknesses: { key: 'swot.weakness', color: 'orange darken-2' },
  opportunities: { key: 'swot.opportunity', color: 'blue darken-2' },
  threats: { key: 'swot.threat', color: 'red darken-2' }
};

export default {
  name: 'FactorInitiativesDrawer',
  props: {
    value: { type: Boolean, default: false },
    factor: { type: Object, default: null },
    // Quadrante do fator: strengths | weaknesses | opportunities | threats
    quadrant: { type: String, default: null },
    initiatives: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    objectives: { type: Object, default: () => ({}) },
    objectivesLoading: { type: Boolean, default: false },
    // Enquanto o formulário da iniciativa está aberto, cliques no diálogo não fecham o painel
    keepOpen: { type: Boolean, default: false }
  },
  computed: {
    typeLabel() {
      const t = FACTOR_TYPES[this.quadrant];
      return t ? this.$t(t.key) : '';
    },
    typeColor() {
      const t = FACTOR_TYPES[this.quadrant];
      return t ? t.color : 'grey';
    }
  },
  methods: {
    statusColor: initiativeStatusColor,
    objectiveLabel(objectiveId) {
      const o = objectiveId != null ? this.objectives[objectiveId] : null;
      return o ? `${o.code} - ${o.description}` : '—';
    }
  }
};
</script>
